import { useEffect, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { LogIn, AlertCircle, ChevronDown, Terminal } from 'lucide-react'
import { useAuth, isSilentAttempt } from '@/lib/auth'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'

const SKIP_AUTH = import.meta.env.VITE_SKIP_AUTH === 'true'
const IS_DEV    = import.meta.env.DEV
const SILENT_FAILED_KEY = 'aip_dns_silent_failed'

export default function LoginPage() {
  const navigate = useNavigate()
  const { isAuthenticated, idpConfig, idpConfigLoading, idpConfigError, login, loginSilent, setToken } = useAuth()
  const [loginError, setLoginError] = useState<string | null>(null)
  const [loginLoading, setLoginLoading] = useState(false)
  const silentTriedRef = useRef(false)

  // Dev-only token paste
  const [devOpen, setDevOpen]     = useState(false)
  const [devToken, setDevToken]   = useState('')
  const [devError, setDevError]   = useState<string | null>(null)

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate({ to: '/dashboard' })
    }
  }, [isAuthenticated, navigate])

  // Auto silent SSO — runs after IDP config has been loaded.
  // Attempts prompt=none to reuse an existing IDP session silently.
  useEffect(() => {
    if (
      SKIP_AUTH ||
      idpConfigLoading ||
      isAuthenticated ||
      silentTriedRef.current ||
      isSilentAttempt() ||
      sessionStorage.getItem(SILENT_FAILED_KEY) === '1' ||
      !idpConfig.authorization_endpoint ||
      !idpConfig.client_id
    ) return

    silentTriedRef.current = true
    sessionStorage.setItem(SILENT_FAILED_KEY, '1')
    loginSilent().catch(() => {
      // loginSilent() redirects; only reaches here on error (rare)
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idpConfigLoading])

  const handleDevToken = () => {
    const raw = devToken.trim()
    if (!raw) { setDevError('Paste a JWT token first'); return }
    // Minimal sanity check — a JWT has three dot-separated base64 segments
    if (raw.split('.').length !== 3) { setDevError('Does not look like a JWT (expected header.payload.signature)'); return }
    setDevError(null)
    setToken(raw)
    navigate({ to: '/dashboard' })
  }

  const handleLogin = async () => {
    setLoginError(null)

    if (!idpConfig.authorization_endpoint || !idpConfig.client_id) {
      setLoginError(
        idpConfigError ||
        'IDP is not configured. Set VITE_IDP_AUTHORIZATION_URL and VITE_IDP_CLIENT_ID, ' +
        'or ensure the backend IDP_ISSUER is set and /auth/info is reachable.'
      )
      return
    }

    setLoginLoading(true)
    try {
      await login()
    } catch (err) {
      setLoginError(err instanceof Error ? err.message : 'Login failed')
      setLoginLoading(false)
    }
  }

  // Show full-page spinner while IDP config is being fetched from the backend
  if (idpConfigLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--bg)]">
        <div className="flex flex-col items-center gap-4">
          <Spinner size="lg" />
          <p className="text-sm text-[var(--fg-muted)]">Loading auth configuration…</p>
        </div>
      </div>
    )
  }

  const displayError = loginError || (idpConfigError && !idpConfig.authorization_endpoint ? idpConfigError : null)

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--bg)] p-4">
      <div className="w-full max-w-md">

        {/* Brand */}
        <div className="mb-8 flex flex-col items-center gap-4">
          <div
            className="flex h-16 w-16 items-center justify-center rounded-2xl"
            style={{ background: 'linear-gradient(135deg, #3053f4 0%, #7c3aed 50%, #ec4899 100%)' }}
          >
            <span className="text-2xl font-bold text-white">AIP</span>
          </div>
          <div className="text-center">
            <h1 className="text-2xl font-bold text-[var(--fg)]">AIP DNS & Certificate Management</h1>
            <p className="mt-1 text-sm text-[var(--fg-muted)]">Infrastructure Management Platform</p>
          </div>
        </div>

        {/* Card */}
        <div className="rounded-xl border border-[var(--border)] bg-[var(--bg-card)] p-8 shadow-2xl">
          <h2 className="mb-6 text-center text-lg font-semibold text-[var(--fg)]">
            Sign in to continue
          </h2>

          <div className="flex flex-col gap-4">
            {displayError && (
              <div className="flex gap-2 rounded-lg border border-[var(--destructive)]/30 bg-[var(--destructive)]/10 p-3">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-[var(--destructive)]" />
                <p className="text-xs text-[var(--destructive)]">{displayError}</p>
              </div>
            )}

            {SKIP_AUTH ? (
              <Button className="w-full" onClick={() => navigate({ to: '/dashboard' })}>
                Continue without auth
              </Button>
            ) : (
              <Button
                className="w-full gap-2"
                onClick={handleLogin}
                disabled={loginLoading || (!idpConfig.authorization_endpoint && !!idpConfigError)}
              >
                {loginLoading ? <Spinner size="sm" /> : <LogIn className="h-4 w-4" />}
                {loginLoading ? 'Redirecting…' : 'Sign in with your organization'}
              </Button>
            )}

            {/* Dev-only token paste — only rendered during `npm run dev` */}
            {IS_DEV && !SKIP_AUTH && (
              <div className="mt-2 rounded-lg border border-dashed border-[var(--border)] bg-[var(--bg)]">
                <button
                  type="button"
                  onClick={() => setDevOpen(o => !o)}
                  className="flex w-full items-center gap-2 px-3 py-2 text-xs text-[var(--fg-muted)] hover:text-[var(--fg)] transition-colors"
                >
                  <Terminal className="h-3.5 w-3.5 shrink-0" />
                  <span className="flex-1 text-left">Dev: paste JWT token</span>
                  <ChevronDown className={`h-3.5 w-3.5 transition-transform ${devOpen ? 'rotate-180' : ''}`} />
                </button>

                {devOpen && (
                  <div className="flex flex-col gap-2 px-3 pb-3">
                    <p className="text-[11px] text-[var(--fg-muted)] leading-snug">
                      IDP redirect not registered for localhost? Get a token from Postman or the
                      cluster and paste it here — it will be stored in localStorage and you can
                      make real authenticated API calls.
                    </p>
                    <textarea
                      rows={4}
                      value={devToken}
                      onChange={e => { setDevToken(e.target.value); setDevError(null) }}
                      placeholder="eyJhbGciOiJSUzI1NiIsInR5cCI6IkpXVCJ9…"
                      className="w-full resize-none rounded border border-[var(--border)] bg-[var(--bg-card)] px-2 py-1.5 font-mono text-[11px] text-[var(--fg)] placeholder:text-[var(--fg-muted)] focus:outline-none focus:ring-1 focus:ring-[var(--accent)]"
                    />
                    {devError && (
                      <p className="text-[11px] text-[var(--destructive)]">{devError}</p>
                    )}
                    <Button size="sm" variant="outline" className="self-end text-xs" onClick={handleDevToken}>
                      Use this token
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-[var(--fg-muted)]">
          AIP Platform &copy; {new Date().getFullYear()} Teradata
        </p>
      </div>
    </div>
  )
}
